<template>
    <transition name="search">
        <div v-if="isSearchOpen" class="overlay" @click="close">
            <ais-index ref="index"
                       @click.native.stop
                       :app-id="appId"
                       :api-key="apiKey"
                       :index-name="indexName"
                       :class-names="{'ais-index': 'search'}"
                       :auto-search="false">

                <header class="search__header">
                    <icon type="search" color="blue" size="24" />
                    <ais-search-box :class-names="{'ais-search-box__form': 'search__form'}">
                        <ais-input ref="input" @query="onQueryChange" placeholder="Search" class="form__input search__input" autofocus />
                    </ais-search-box>
                    <ais-stats class="search__stats">
                        <template slot-scope="{ totalResults }">
                            Showing <span class="text--dark">{{ totalResults }} results</span>
                        </template>
                    </ais-stats>
                    <a @click.prevent="close" href="#" class="icon-button search__close">
                        <icon type="close" color="gray" size="20" />
                    </a>
                </header>

                <ais-results ref="results" :class-names="{'ais-results': 'search__results'}">
                    <a @mouseover="highlightResult(index)" :href="result.url" slot-scope="{ result, index }" class="search__result" :class="{ active : isHighlighted(index) }">
                        <ais-highlight attribute-name="title" :result="result" />
                    </a>
                </ais-results>

                <ais-no-results class="search__noresults">
                    <template slot-scope="props">
                        <p>Sorry, no jobs could be found for <strong class="text--highlight">{{ props.query }}</strong></p>
                    </template>
                </ais-no-results>

            </ais-index>
        </div>
    </transition>
</template>

<script>
import { mapState } from 'vuex'
import { CLOSE_SEARCH } from '../../stores/global/mutations'
import Icon from '../Global/Icon'

export default {
    name: 'Search',

    props: {
        tenant: {
            type: Object,
            required: true
        },

        appId: {
            type: String,
            required: true
        },

        apiKey: {
            type: String,
            required: true
        }
    },

    data () {
        return {
            highlightedResult: 0
        }
    },

    methods: {
        close () {
            this.$store.commit(CLOSE_SEARCH)
            this.$refs.input.$el.removeEventListener('keyup', this.onKeyUp)
            this.$refs.input.$el.removeEventListener('keydown', this.onKeyDown)
        },

        up () {
            if (this.highlightedResult === 0) {
                this.highlightResult(this.$refs.results.$el.children.length - 1)
                return
            }

            this.highlightResult(this.highlightedResult - 1)
        },

        down () {
            if (this.highlightedResult === (this.$refs.results.$el.children.length - 1)) {
                this.highlightResult(0)
                return
            }

            this.highlightResult(this.highlightedResult + 1)
        },

        goToResult () {
            window.location = this.$refs.results.$el.children[this.highlightedResult].href
        },

        maybeClose () {
            if (this.$refs.input.query) {
                this.$refs.input.query = ''
                return
            }
            this.close()
        },

        onKeyUp (event) {
            if (this.$refs.results.$el.children === undefined) {
                return
            }

            this.$nextTick(() => {
                switch (event.key) {
                case 'ArrowUp':
                    this.up()
                    break
                case 'ArrowDown':
                    this.down()
                    break
                }
            })
        },

        onQueryChange () {
            this.resetHighlight()
        },

        onKeyDown (event) {
            if (event.key === 'Enter') {
                event.preventDefault()

                this.goToResult()
            }

            if (event.key === 'Escape') {
                event.preventDefault()

                this.maybeClose()
            }
        },

        isHighlighted (index) {
            return index === this.highlightedResult
        },

        highlightResult (index) {
            this.highlightedResult = index
            let highlightedElement = this.$refs.results.$el.children[this.highlightedResult]

            if (this.$refs.results.$el.scrollTop > highlightedElement.offsetTop) {
                this.$refs.results.$el.scrollTop = highlightedElement.offsetTop
            }

            if ((highlightedElement.offsetTop + highlightedElement.offsetHeight) > (this.$refs.results.$el.offsetHeight)) {
                this.$refs.results.$el.scrollTop = highlightedElement.offsetTop
            }
        },

        resetHighlight () {
            this.highlightedResult = 0
            this.$refs.results.$el.scrollTop = 0
        }
    },

    watch: {
        isSearchOpen (open) {
            if (open) {
                this.$nextTick(() => {
                    this.$refs.input.$el.addEventListener('keyup', this.onKeyUp)
                    this.$refs.input.$el.addEventListener('keydown', this.onKeyDown)

                    this.$refs.input.$el.focus()
                })
            }
        }
    },

    computed: {
        ...mapState(['isSearchOpen']),

        indexName () {
            return `${this.tenant.name}_jobs`
        }
    },

    components: {
        Icon
    }
}
</script>

<style scoped lang="less">
    .overlay {
        transition: all 0.3s ease-in-out;
    }

    .search-enter, .search-leave-to {
        opacity: 0;
    }
</style>
